import { Space } from 'antd';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from 'tsyringe';

import datetime, { TimeUnit } from '@/infra/adapters/datetime';
import { Menu, SystemType } from '@/v1/main/domain/auth';
import { AuthServiceImpl } from '@/v1/main/usecases';
import { useIdle } from '@/v1/main/views/Hook';
import {
    type ModalOption,
    ModalType,
    useModal,
} from '@/v1/main/views/Provider';
import { CountdownText } from '@/v1/main/views/Typography';

interface AuthContextProps {
    nickname: string; // navbar usage
    pageId: string;
    menus: Menu[]; // sidebar usage
    loggedIn: boolean; // route
    setLoggedIn: (value: boolean) => void; // login or logout usage
    setPageId: (value: string) => void;
    setSystemType: (value: SystemType) => Promise<void>;
    isValidAction: (id: string) => boolean; // showing actions
}

interface AuthProviderProps {
    children: React.ReactNode;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const usecase = container.resolve(AuthServiceImpl);
    const { t } = useTranslation(['interface']);
    const { setModalOption, destroy } = useModal();
    const { isIdle } = useIdle();

    const [loggedIn, setLoggedIn] = useState<boolean>(usecase.getLoginState());

    const [nickname, setNickname] = useState<string>('');
    const [menus, setMenus] = useState<Menu[]>([]);
    const [pageId, setPageId] = useState<string>('');

    const [actionMap, setActionMap] = useState<Map<string, boolean>>(new Map());

    const isValidAction = (actionId: string) =>
        actionMap.get(pageId.padStart(2, '0') + actionId.padStart(2, '0')) ||
        false;

    const refresh = async () => {
        try {
            await usecase.refreshToken();
        } catch (err: unknown) {
            setLoggedIn(false);
        }
    };

    const login = async () => {
        try {
            const { nickname, menus, actions } =
                await usecase.getUserInterface();
            const map = new Map();
            for (const action of actions) {
                map.set(action, true);
            }
            setActionMap(map);
            setNickname(nickname);
            setMenus(menus);
        } catch (err: unknown) {
            setLoggedIn(false);
        }
    };

    const logout = async () => {
        setPageId('');
        setNickname('');
        setActionMap(new Map());
        setMenus([]);
        await usecase.logout();
    };

    const setSystemType = async (value: SystemType) => {
        await usecase.logout();
        setLoggedIn(false);
        usecase.setSystemType(value);
    };

    const detectInactivity = () => {
        const onOk = () => {
            destroy();
            refresh();
        };
        const onCancel = () => {
            destroy();
            setLoggedIn(false);
        };
        const now = datetime.now();
        const deadline = now.add(5, TimeUnit.MIN);
        const title = (
            <Space>
                <CountdownText
                    type="warning"
                    value={deadline}
                    onFinish={onCancel}
                />
                {t('notification.detect_if_user_is_idle.title', {
                    ns: 'interface',
                })}
            </Space>
        );
        const opt: ModalOption = {
            type: ModalType.INFO,
            title: title,
            okText: t('action.yes', { ns: 'interface' }),
            cancelText: t('action.no', { ns: 'interface' }),
            onOk,
            onCancel,
        };
        setModalOption(opt);
    };

    useEffect(() => {
        if (loggedIn && isIdle) {
            detectInactivity();
        }
    }, [isIdle]);

    useEffect(() => {
        if (loggedIn) {
            login();
        } else {
            logout();
        }
    }, [loggedIn]);

    return (
        <AuthContext.Provider
            value={{
                nickname,
                pageId,
                menus,
                loggedIn,
                setLoggedIn,
                setPageId,
                isValidAction,
                setSystemType,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within a AuthProvider');
    }
    return context;
};
